import { CardioOrResistance, GymTypes, Level } from 'fitbeat.models';
import {
  AudioSoundEvent,
  CustomIconName,
  Exercise,
  Phase,
  ScheduleEntryColourCode,
  SessionSequence,
  UserExerciseSession,
  WorkoutSettings,
} from '../../entities';
import { ISessionSequenceStrategy } from './isessionsequencestrategy';
import { getDefaultExerciseVersion } from '../../config';
import { isBoxingWorkout } from '../workoutselection';

export class FunctionalStaticSequence implements ISessionSequenceStrategy {
  public getSessionSequence(
    exercise: Exercise,
    alernativeExercise: Exercise,
    secondAternativeExercise: Exercise,
    thirdAlternateExercise: Exercise,
    zone: number,
    quarter: number,
    level?: Level,
    workoutSettings?: WorkoutSettings,
    previousExerciseSessions?: UserExerciseSession[],
  ): SessionSequence[] {
    const isBoxing = isBoxingWorkout(previousExerciseSessions);

    const stageIndex = previousExerciseSessions
      ? previousExerciseSessions[0].getIndexForCurrentExercise()
      : undefined;
    const isCurrentExerciseRepeated = stageIndex !== undefined
      && stageIndex % 2 === 1;

    const transitionPhase = {
      displayBurnOrBuildIcon: true,
      progressColourCode: ScheduleEntryColourCode.Rest,
      progressText: isCurrentExerciseRepeated ? 'Rest & Repeat in {seconds}' : 'Start in {seconds}',
      customIcon: isCurrentExerciseRepeated ? CustomIconName.Repeat : undefined,
      showProcessedMetrics: false,
      showCalculatedMetrics: true,
      phase: Phase.Rest,
      zone,
    };

    const transitionPhase1 = {
      ...transitionPhase,
      durationInMilliseconds: 20,
      videoFile: exercise.uniqueReferenceNumber,
      exerciseVersion: exercise._rev,
      displayName: exercise.name,
      tips: Exercise.getFormattedTips(exercise),
      equipmentWeightInfo: exercise.equipmentWeightInfo,
      audioSoundEvent: undefined,
    };

    const transitionPhase2 = {
      ...transitionPhase,
      durationInMilliseconds: 16,
      videoFile: alernativeExercise.uniqueReferenceNumber,
      exerciseVersion: alernativeExercise._rev,
      displayName: alernativeExercise.name,
      tips: Exercise.getFormattedTips(alernativeExercise),
      equipmentWeightInfo: alernativeExercise.equipmentWeightInfo,
      audioSoundEvent: AudioSoundEvent.startOfRestPeriod,
    };

    const workoutPhase = {
      displayBurnOrBuildIcon: true,
      progressColourCode: ScheduleEntryColourCode.Play,
      progressText: 'Train for {seconds}',
      showProcessedMetrics: false,
      showCalculatedMetrics: true,
      audioSoundEvent: AudioSoundEvent.startOfWorkout,
      phase: Phase.Train,
      zone,
    };

    const workoutPhase1 = {
      ...workoutPhase,
      durationInMilliseconds: 40,
      videoFile: exercise.uniqueReferenceNumber,
      exerciseVersion: exercise._rev,
      displayName: exercise.name,
      tips: Exercise.getFormattedTips(exercise),
      equipmentWeightInfo: exercise.equipmentWeightInfo,
    };

    const workoutPhase2 = {
      ...workoutPhase,
      durationInMilliseconds: 40,
      videoFile: alernativeExercise.uniqueReferenceNumber,
      exerciseVersion: alernativeExercise._rev,
      displayName: alernativeExercise.name,
      tips: Exercise.getFormattedTips(alernativeExercise),
      equipmentWeightInfo: alernativeExercise.equipmentWeightInfo,
    };

    const zoneEnd = {
      durationInMilliseconds: 4,
      videoFile: SessionSequence.getPauseVideoFileName(
        workoutSettings?.cardioOrResistance ?? CardioOrResistance.Resistance,
        GymTypes.Indoor,
        isBoxing,
      ),
      exerciseVersion: getDefaultExerciseVersion(),
      displayName: exercise.name,
      displayBurnOrBuildIcon: false,
      progressColourCode: ScheduleEntryColourCode.Rest,
      progressText: 'Tidy up as you go',
      showProcessedMetrics: false,
      showCalculatedMetrics: true,
      audioSoundEvent: AudioSoundEvent.endOfWorkoutSession,
      tips: [],
      phase: Phase.Rest,
      zone,
    };

    return [{
      durationInMilliseconds: 20,
      sequence: [transitionPhase1],
    }, {
      durationInMilliseconds: 40,
      sequence: [workoutPhase1],
    }, {
      durationInMilliseconds: 16,
      sequence: [transitionPhase2],
    }, {
      durationInMilliseconds: 40,
      sequence: [workoutPhase2],
    }, {
      durationInMilliseconds: 4,
      sequence: [zoneEnd],
    }];
  }
}
